import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useCallback, useEffect, useState }  from 'react';
import { withStylesPropTypes } from 'react-with-styles';

import { useAddressAutofillCore, useGeocodingCore } from '@mapbox/search-js-react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import CustomTextField from './CustomTextField';
import CustomInputLabel from './CustomInputLabel';
import CustomHelperText from './CustomHelperText';

const defaultFormData = {
  address_line_1: '',
  address_line_2: '',
  address_city: '',
  address_state: '',
  address_zip: '',
};

const handleAddressAutofill = ({autofill, setSuggestions, onAddressChange}) => async e => {
  const text = e?.target?.value;
  if (!text) return;
  const response = await autofill.suggest(text, {
    // https://docs.mapbox.com/mapbox-search-js/api/react/autofill/
    sessionToken: 'test-123'
  });
  const suggestions = (response?.suggestions || []);
  const addresses = suggestions.map(s => ({
    address_line_1: s?.address_line1,
    address_line_2: s?.address_line2,
    address_city: s?.address_level2,
    address_state: s?.address_level1,
    address_zip: s?.postcode,
    full_address: s?.full_address,
  }));
  setSuggestions(addresses);
  if (addresses?.[0]) onAddressChange(omit(addresses[0], 'full_address'));
};

const handleFieldChange = ({formData, setFormData, onChange}) => field => e => {
  const newVal = {...formData, [field]: e.target.value};
  setFormData(newVal);
  if (onChange) onChange(newVal);
};

const handleAddressChange = ({formData, setFormData, onChange}) => address => {
  const newVal = {...formData, ...address};
  setFormData(newVal);
  if (onChange) onChange(newVal);
};

const handleGeocode = ({geocoding, setCoordinates, onChange, formData}) => async ({suggestion}) => {
  const fullAddress = suggestion?.full_address;
  if (!fullAddress) return;
  const response = await geocoding.forward(fullAddress, {limit: 1});
  const lat = response?.features?.[0]?.geometry?.coordinates?.[1];
  const lon = response?.features?.[0]?.geometry?.coordinates?.[0];
  setCoordinates({lat, lon});
  if (onChange) onChange({...formData, lat, lon});
};

const AddressForm = props => {
  const {
    useGeocoding,
    onChange,
    editable, 
    defaultValue,
    helperText,
    error,
    showGeocoding = false,
    showAddress = true,
  } = props;
  console.log('AddressForm.defaultValue', defaultValue);
  const [formData, setFormData]  = useState(defaultValue || defaultFormData);
  const [showEditor, setShowEditor] = useState(false);
  const [coordinates, setCoordinates] = useState(
    defaultValue && defaultValue.lat && defaultValue.lon
    ? {lat: defaultValue.lat, lon: defaultValue.lon}
    : {lat: null, lon: null}
  );
  const [suggestions, setSuggestions] = useState([]);
  const [address, setAddress] = useState(
    defaultValue 
    ? pick(
        defaultValue, 
        'address_line_1', 'address_line_2', 
        'address_city', 'address_state', 'address_zip'
    ) 
    : {
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_state: '',
    address_zip: '',
  });
  const autofill = useAddressAutofillCore({accessToken: config().mapBoxPublicApiKey});
  const geocoding = useGeocodingCore({ accessToken: config().mapBoxPublicApiKey });
  const topSuggestion = suggestions?.[0];
  const onGeocode = handleGeocode({geocoding, setCoordinates, formData, onChange});
  const onFieldChange = handleFieldChange({formData, setFormData, onChange});
  useEffect(() => {
    if (useGeocoding && topSuggestion) onGeocode({suggestion: topSuggestion, });
  }, [useGeocoding, topSuggestion, geocoding]);
  if (props?.useGeocoding && suggestions?.[0]) handleGeocode({suggestions, geocoding});
  const onSubmit = data => console.log('onSubmit', data);
  const onAddressChange = handleAddressChange({
    formData, 
    setFormData, 
    onChange
  });
  const onTextChange = handleAddressAutofill({autofill, setSuggestions, onAddressChange});
  const debouncedTextChangeHandler = useCallback(
    debounce(onTextChange, 500),
    [onTextChange]
  );
  // console.log('suggestions', suggestions);
  return (
    <Stack spacing={2}>
      {
        (props.showLabel || props?.label)
        && <CustomInputLabel required={props?.required || false}>
          {props?.label || 'Address'}
        </CustomInputLabel>
      }
      <Autocomplete
        type='text'
        freeSolo
        color='secondary'
        options={(suggestions || []).map(s => ({
          label: `${s.address_line_1}, ${s.address_city}, ${s.address_state} ${s.address_zip}`,
          value: s.id,
        }))}
        filterOptions={(x) => x}
        defaultValue={defaultValue?.address_line_1 || ''}
        onChange={(e,val,reason) => {
            if (reason === 'selectOption') {
              if (val.id) return onSubmit(val.id, val.id);
              if (val.value) return onSubmit(val.value, val.value);
            }
            debouncedTextChangeHandler(e);
          }
        }
        renderInput={params => (
          <TextField
            style={{backgroundColor: 'white'}}
            placeholder={props?.placeholder || 'Search Address'}
            onChange={debouncedTextChangeHandler}
            error={error}
            {...params}
            size='small'
            fullWidth={true}
            minWidth={400}
            InputProps={{
              ...params.InputProps,
              autoFocus: !!props?.autoFocus,
              color: props?.color,
              startAdornment: <InputAdornment position="start">
                <SearchIcon 
                  style={{color: config().colors[props.color], fontSize: '1.2rem'}} 
                />
              </InputAdornment>,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {
        !!error &&
        <Alert severity='error' variant='outlined'>
          {helperText || 'Please enter a session configuration.'}
        </Alert>
      }
      {
        !!helperText && !error &&
        <CustomHelperText>{helperText}</CustomHelperText>
      }
      {
        showAddress && !showEditor && (topSuggestion || props.defaultValue) &&
        <Box 
          sx={{bgcolor: 'white', borderRadius: '3px', padding: 2, border: '1px solid #e1e1e1'}}
        >
          <Typography color='gray' fontWeight='700'>Address Found</Typography>
          <Typography color='gray'>{formData.address_line_1}</Typography>
          <Typography color='gray'>{formData.address_line_2 && `${formData.address_line_2}`}</Typography>
          <Typography color='gray'>
            {formData.address_city && `${formData.address_city}`}
            {formData.address_state && `, ${formData.address_state}`}
            {formData.address_zip && `, ${formData.address_zip}`}
          </Typography>
          {showGeocoding && coordinates?.lat && coordinates?.lon && (
            <Typography color='gray'>
              Lat: {coordinates.lat.toFixed(4)}, Lon: {coordinates.lon.toFixed(4)}
            </Typography>
          )}
        </Box>
      }
      {
        editable && !showEditor &&
        <Button 
          variant='text'
          size='small'
          onClick={() => setShowEditor(true)}
        >
          Edit Address Details
        </Button>
      }
      {editable && showEditor &&
        <Grid container fluid margin={0} padding={0} spacing={1} style={{marginLeft: '-0.5em'}}>
          <Grid item xs={12}>
            <CustomTextField
              name="address_line_1"
              required
              fullWidth
              size='small'
              label="Street Address"
              variant='outlined'
              placeholder="123 Main St"
              type="text"
              autoComplete="street-address"
              value={formData.address_line_1}
              onChange={onFieldChange('address_line_1')}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              name="address_line_2"
              fullWidth
              size='small'
              label="Unit / Suite"
              variant='outlined'
              placeholder="Unit #1"
              type="text"
              autoComplete="address-line2"
              value={formData.address_line_2}
              onChange={onFieldChange('address_line_2')}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              name="address_city"
              required
              fullWidth
              size='small'
              label="City"
              variant='outlined'
              placeholder="Portland"
              type="text"
              autoComplete="address-level2"
              value={formData.address_city}
              onChange={onFieldChange('address_city')}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomTextField
              name="address_state"
              required
              size='small'
              label="State"
              variant='outlined'
              placeholder="OR"
              type="text"
              autoComplete="address-level1"
              value={formData.address_state}
              onChange={onFieldChange('address_state')}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomTextField
              name="address_zip"
              required
              size='small'
              label="Postal Code"
              variant='outlined'
              placeholder="54321"
              type="text"
              autoComplete="postal-code"
              value={formData.address_zip}
              helperText='5-digit zip code'
              onChange={onFieldChange('address_zip')}
            />
          </Grid>
        </Grid>}
    </Stack>
  );
};

AddressForm.defaultProps = {
  editable: true,
  showLabel: false,
  color: 'primary',
  autoFocus: true,
};

AddressForm.propTypes = {
  useGeocoding: PropTypes.bool,
  onChange: PropTypes.func,
  editable: PropTypes.bool,
  showLabel: PropTypes.bool,
  autoFocus: PropTypes.bool,
  color: PropTypes.string,
  defaultValue:   PropTypes.shape({
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    address_city: PropTypes.string,
    address_state: PropTypes.string,
    address_zip: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
};

export default AddressForm;